<template>
  <v-container class="py-6">
    <v-row>
      <v-col cols="12" md="8">
        <ExecutiveReports />
      </v-col>
      <v-col cols="12" md="4">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" start>mdi-speedometer</v-icon>
              KPIs en Tiempo Real
            </div>
            <v-chip size="x-small" :color="isConnected ? 'success' : 'error'" variant="flat">
              {{ isConnected ? 'Conectado' : 'Desconectado' }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <!--
              P13-V5A-R3 · DIRECTOR_REPAIR_R1 — the canonical analytics socket admits super_admin
              only. A refusal is shown as a refusal; it is never absorbed into a KPI of zero.
            -->
            <v-alert
              v-if="realtimeAnalyticsDenied"
              data-testid="realtime-reports-analytics-denied"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              La telemetría de analítica en tiempo real no está disponible para su rol; las cifras
              derivadas de ella no se muestran.
            </v-alert>
            <v-alert
              v-else-if="realtimeState === 'error'"
              data-testid="realtime-reports-analytics-error"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              No se pudo verificar la analítica en tiempo real. No se muestran cifras: un fallo de
              transporte no autoriza a estimarlas.
            </v-alert>
            <div
              v-else-if="realtimeState === 'resolving'"
              data-testid="realtime-reports-analytics-resolving"
              class="text-caption text-medium-emphasis py-2"
            >
              Verificando disponibilidad de analíticas en tiempo real…
            </div>
            <!--
              P13-V5A-R5 — la telemetría se interrumpió después de haber publicado. No se
              muestran las últimas cifras conocidas: esta tarjeta se titula "en Tiempo Real" y
              ningún contrato de producto define aquí una presentación de dato obsoleto.
            -->
            <v-alert
              v-else-if="realtimeState === 'stale'"
              data-testid="realtime-reports-analytics-stale"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              Se perdió la telemetría en tiempo real. Las cifras dejaron de estar vigentes y no
              se muestran; se restablecerán cuando el canal vuelva a responder.
            </v-alert>

            <!--
              P13-V5A-R3 · DIRECTOR_MICRO_REPAIR_R3 — ONLY `ready` may publish a figure.
              R2 made a denial terminal, but the card still began implicitly permitted: a
              Math.random-seeded trend, `Alertas 0` and `SLA 97%` were on screen until a denial
              arrived and removed them. Authorization uncertainty is not permission, so the gate is
              now the positive state rather than the absence of a refusal.
            -->
            <template v-if="canPublishRealtime">
              <v-row class="mb-2" align="center">
                <v-col cols="6">
                  <v-switch v-model="autoUpdate" hide-details inset color="primary" label="Auto-actualizar" />
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-btn size="small" variant="text" @click="manualRefresh">
                    <v-icon start>mdi-refresh</v-icon>Actualizar
                  </v-btn>
                </v-col>
              </v-row>

              <div class="text-caption text-medium-emphasis mb-1">Eventos por minuto</div>
              <RealtimeSparkline :points="kpiTrend" :height="48" />

              <!--
                P13-V5A-R5 — ninguna de estas dos cifras la prueba el canal que otorga READY:
                TELEMETRY_KEYS (stores/realtime-analytics.ts:45-53) no contiene ni alertas ni
                SLA. Se publican SÓLO si la telemetría las trae; un cero real sigue siendo 0.
              -->
              <v-row class="mt-2">
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Alertas</div>
                  <div v-if="alerts === null" class="text-body-2 text-medium-emphasis" data-testid="realtime-alerts-absent">
                    Sin datos
                  </div>
                  <div v-else class="text-h6" :class="alertsClass" data-testid="realtime-alerts-value">{{ alerts }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">SLA</div>
                  <div v-if="sla === null" class="text-body-2 text-medium-emphasis" data-testid="realtime-sla-absent">
                    Sin datos
                  </div>
                  <div v-else class="text-h6" :class="slaClass" data-testid="realtime-sla-value">{{ sla }}%</div>
                </v-col>
              </v-row>

              <v-expansion-panels class="mt-2" variant="accordion" density="compact">
                <v-expansion-panel title="Umbrales">
                  <v-expansion-panel-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="alertThreshold" type="number" min="0" max="9999" density="compact" label="Alerta ≥" hide-details />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model.number="slaMin" type="number" min="0" max="100" density="compact" label="SLA mínimo %" hide-details />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-btn block color="primary" variant="tonal" class="mt-3" @click="exportKPIs">
                <v-icon start>mdi-download</v-icon> Exportar KPIs
              </v-btn>
            </template>
          </v-card-text>
        </v-card>
        <!-- Resumen Ejecutivo Inventario (Enterprise 7.1) -->
        <v-card elevation="2" class="mt-4">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" start>mdi-warehouse</v-icon>
              Inventario · Resumen Ejecutivo
            </div>
            <v-btn icon variant="text" size="small" :loading="invLoading" @click="loadExecutiveInventory()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="invError" type="error" density="compact" class="mb-2">{{ invError }}</v-alert>
            <div v-if="invError && (invDebugUrl || invDebugStatus)" class="text-caption text-medium-emphasis mb-2" style="word-break: break-all;">
              <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
              {{ invDebugMethod || 'GET' }} {{ invDebugStatus ? '(' + invDebugStatus + ')' : '' }} · {{ invDebugUrl }}
            </div>
            <v-skeleton-loader v-if="invLoading && !inv" type="list-item-two-line" class="mb-2" />
            <!--
              P13-V7B (refutation): every figure is read where GET /inventory/reports/executive publishes
              it (reportsController.getExecutiveReport: `summary.{totalItems,totalStock,lowStockItems}`,
              `alerts.summary.total`, `categories[]`). "Alertas Activas" rendered `alerts.active` — the
              ARRAY of sampled alerts — "Bajo Stock" read `alerts.lowStock`, a field nobody sends (so it
              was always 0), and "Top categorías" read `topCategories` (the producer sends
              `categories`), so it was always empty. A figure the producer did not state is "Sin datos".
            -->
            <template v-if="inv">
              <v-row>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Ítems</div>
                  <div class="text-h6" data-testid="rt-inv-total-items">{{ statText(invFigures.totalItems) }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Stock Total</div>
                  <div class="text-h6" data-testid="rt-inv-total-stock">{{ statText(invFigures.totalStock) }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Alertas Activas</div>
                  <div class="text-h6" :class="(invFigures.activeAlerts ?? 0) > 0 ? 'text-error' : ''" data-testid="rt-inv-active-alerts">{{ statText(invFigures.activeAlerts) }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text-medium-emphasis">Bajo Stock</div>
                  <div class="text-h6" data-testid="rt-inv-low-stock">{{ statText(invFigures.lowStockItems) }}</div>
                </v-col>
              </v-row>
              <div class="text-caption text-medium-emphasis mt-1">Top categorías (por valor)</div>
              <div v-if="invFigures.categories === null" class="text-body-2 text-medium-emphasis" data-testid="rt-inv-categories-absent">Sin datos</div>
              <div v-else-if="invFigures.categories.length === 0" class="text-body-2 text-medium-emphasis">Sin categorías con inventario</div>
              <v-chip-group v-else column>
                <v-chip v-for="c in invFigures.categories.slice(0,3)" :key="c.category" size="x-small" class="ma-1" color="primary" variant="tonal" data-testid="rt-inv-category">
                  {{ c.category }} ({{ statText(c.totalStock) }})
                </v-chip>
              </v-chip-group>
            </template>
          </v-card-text>
        </v-card>

        <!-- Reportes Detallados (7.2): Alertas. Las pestañas de Categorías y Movimientos
             fueron RETIRADAS en el cierre terminal V2B: sus rutas backend servían datos
             SIMULADOS como reporte operativo (hoy responden 410) — ninguna pantalla activa
             promete un reporte sin fuente real autorizada. -->
        <v-card elevation="2" class="mt-4">
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" start>mdi-view-dashboard-variant</v-icon>
              Reportes de Inventario
            </div>
            <div>
              <v-btn size="small" variant="text" @click="loadAlertsReport()">
                <v-icon start>mdi-refresh</v-icon>Actualizar
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <!-- P13-V7B (refutation): the tab counted "(0)" when the alerts read had failed or not yet
                 answered. The count is the server's `summary.total`, or nothing. -->
            <v-tabs v-model="invTabs" density="compact" bg-color="transparent" grow>
              <v-tab value="alerts" data-testid="rt-alerts-tab">Alertas<template v-if="alertsSummary"> ({{ statText(alertsSummary.total) }})</template></v-tab>
            </v-tabs>

            <v-window v-model="invTabs" class="mt-3">
              <!-- Alertas -->
              <v-window-item value="alerts">
                <v-row dense class="mb-2">
                  <v-col cols="7">
                    <v-select
                      v-model="alSeverity"
                      :items="[
                        { title: 'Todas', value: '' },
                        { title: 'Info', value: 'info' },
                        { title: 'Warning', value: 'warning' },
                        { title: 'Critical', value: 'critical' }
                      ]"
                      label="Severidad"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="5" class="d-flex align-center">
                    <v-switch v-model="alResolved" density="compact" hide-details color="primary" inset label="Resueltas" />
                  </v-col>
                </v-row>
                <v-alert v-if="alError" type="error" density="compact" class="mb-2">{{ alError }}</v-alert>
                <v-skeleton-loader v-if="alLoading && !alertsReport" type="table" class="mb-2" />
                <!-- P13-V7B (refutation): with no report the line read "Total: 0 · Críticas: 0 · …" —
                     a failed read published as an empty warehouse. It now says there is no data. -->
                <div v-else-if="!alertsReport" class="text-body-2 text-medium-emphasis" data-testid="rt-alerts-absent">
                  Sin datos
                </div>
                <div v-else>
                  <div class="text-caption text-medium-emphasis mb-2" data-testid="rt-alerts-summary">
                    Total: {{ statText(alertsSummary?.total ?? null) }} · Críticas: {{ statText(alertsSummary?.critical ?? null) }} · Warning: {{ statText(alertsSummary?.warning ?? null) }} · Info: {{ statText(alertsSummary?.info ?? null) }}
                  </div>
                  <v-table density="compact">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Tipo</th>
                        <th>Severidad</th>
                        <th>Ítem</th>
                        <th>Stock</th>
                        <th>Umbral</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="a in (alertsReport?.alerts || [])" :key="a.id">
                        <td>{{ a.id }}</td>
                        <td>{{ a.type }}</td>
                        <td>{{ a.severity }}</td>
                        <td>{{ a.product?.name }}</td>
                        <td>{{ a.product?.currentStock }}</td>
                        <td>{{ a.product?.minStock }}</td>
                        <td>{{ a.createdAt }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                  <v-btn block color="primary" variant="tonal" class="mt-2" @click="exportAlertsCsv()">
                    <v-icon start>mdi-download</v-icon> Exportar CSV
                  </v-btn>
                </div>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <v-snackbar v-model="notify" :color="notifyColor" timeout="3000">
    <v-icon start>mdi-bell-alert</v-icon>
    {{ notifyText }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import ExecutiveReports from '@/components/reports/ExecutiveReports.vue'
import RealtimeSparkline from '@/components/analytics/RealtimeSparkline.vue'
import { useRealtimeStore } from '@/stores/realtime'
import websocketService from '@/services/websocket.service'
// The canonical LOCAL recovery signal (P13-V1D R1/R2): emitted exactly once per PHYSICAL
// recovery, never for the initial connection, and it also covers connect_error -> later connect.
import { RT_LOCAL } from '@/services/realtime-events'
// The SAME admissibility contract the realtime store enforces — not a second validator.
import { normalizeTelemetry } from '@/stores/realtime-analytics'
import { InventoryService } from '@/services/api'

/**
 * ── P13-V5A-R5 · NO FIGURE WITHOUT CURRENT TELEMETRY ──
 *
 * R3 established WHEN this card may begin publishing. It did not establish that what it
 * publishes was ever measured, nor that it stops when the measurement does. Three residues:
 *
 *  1. THE SIMULATOR SURVIVED READY, AND DISCONNECTION WAS ITS TRIGGER. `startSimulatorIfAllowed`
 *     armed a 1.5 s `setInterval` at the ready transition whose body ran `if (!isConnected) tick()`,
 *     and `tick()` random-walked `kpiTrend`, `alerts` and `sla`. So the card displayed the chip
 *     "Desconectado" while its three KPIs kept advancing — the strongest possible statement that
 *     the figures were not measurements. The simulator is DELETED, not gated: telemetry is the
 *     only producer this card has.
 *  2. NOTHING COULD DEMOTE A READY CARD. The complete set of writes to `realtimeState` was
 *     resolving/unauthorized/error/ready, and `failRealtimeAnalytics` early-returned unless the
 *     state was still `resolving` — so once ready, telemetry loss was structurally
 *     unrepresentable. `stale` closes that hole.
 *  3. `alerts` AND `sla` WERE NEVER PROVEN. They were seeded 0 and 97, and readiness is granted
 *     by any payload carrying one of the seven TELEMETRY_KEYS — a list containing neither an
 *     alerts figure nor an SLA figure (stores/realtime-analytics.ts:45-53). R3 used exactly this
 *     argument to refuse the timeseries channel readiness, but the same gap applies to the
 *     metrics channel: a valid payload that omits them left `Alertas 0` and `SLA 97%` on screen
 *     as measurements. 97 in particular was never measured by anything, ever. Both now start as
 *     `null` and render an explicit absence until a payload states them.
 *
 * A real zero still publishes as zero: `null` means "not stated", not "nothing happened".
 */
const kpiTrend = ref<number[]>([])
const alerts = ref<number | null>(null)
const sla = ref<number | null>(null)

/**
 * ── P13-V5A-R3 · DIRECTOR_MICRO_REPAIR_R3 · THE REALTIME CARD STARTS FAIL-CLOSED ──
 *
 * The previous repair made a DENIAL terminal, but the card still began in an implicitly permitted
 * display state: `realtimeAnalyticsDenied = false` at creation, `kpiTrend` seeded with 20
 * `Math.random()` points on mount, `alerts` defaulting to 0 and `sla` to 97. So for a bounded actor
 * the real sequence was:
 *
 *     mount -> synthetic/default figures visible -> socket round-trip -> { denied:true } -> gone
 *
 * That is fail-OPEN reporting: authorization UNCERTAINTY was being treated as permission.
 *
 * The card now runs an explicit state machine, and only ONE state may publish a figure:
 *
 *     resolving  ──valid telemetry──▶ ready
 *                ──{ denied:true } ─▶ unauthorized      (terminal)
 *                ──transport/contract failure──▶ error
 *
 * `resolving` is the initial state. No default, cached, seeded or simulated value is publishable
 * until an admissible realtime analytics response establishes the current acquisition as ready.
 *
 * WHAT COUNTS AS PROOF. Readiness is established by the METRICS channel only. The canonical socket
 * emits `{ metric, duration, data }` for timeseries (`realtimeSocketServer.js:339`) — it carries no
 * alerts and no SLA, so it cannot prove those figures; treating it as readiness would surface the
 * 0 / 97 defaults as measurements. Admissibility itself is delegated to `isTelemetryPayload` /
 * `normalizeTelemetry`, the SAME validator the realtime store uses, so this view does not become a
 * second contract authority. Connection status, socket "connected" and elapsed time prove nothing.
 *
 * This is NOT a frontend authorization decision: the view never inspects a role. It simply refuses
 * to publish until the SERVER has produced an admissible answer.
 */
type RealtimeCardState = 'resolving' | 'ready' | 'unauthorized' | 'error' | 'stale'
const realtimeState = ref<RealtimeCardState>('resolving')

/** Retained for the existing denial markup/tests; derived from the single state. */
const realtimeAnalyticsDenied = computed(() => realtimeState.value === 'unauthorized')
/** The only state in which this card may publish a figure. */
const canPublishRealtime = computed(() => realtimeState.value === 'ready')

/** How long the card waits for ANY admissible answer before calling the acquisition failed. */
const RESOLUTION_TIMEOUT_MS = 8000

let resolutionTimeout: any = null

/** Every exit from a publishing state drops the figures it was publishing. */
function dropFigures() {
  kpiTrend.value = []
  alerts.value = null
  sla.value = null
}

function clearResolutionTimeout() {
  if (resolutionTimeout) { clearTimeout(resolutionTimeout); resolutionTimeout = null }
}

/** Terminal for this view: drop the figures, keep only the refusal. */
function refuseRealtimeAnalytics() {
  realtimeState.value = 'unauthorized'
  dropFigures()
  clearResolutionTimeout()
}

/** The acquisition could not be completed. Not a denial, and certainly not a licence to simulate. */
function failRealtimeAnalytics() {
  if (realtimeState.value !== 'resolving') return
  realtimeState.value = 'error'
  dropFigures()
  clearResolutionTimeout()
}

/**
 * P13-V5A-R5 — telemetry stopped after the card had been publishing.
 *
 * This is the transition the R3 state machine could not express. It is deliberately NOT a
 * "last known value" display: §25 requires that a stale figure stop changing AND be labelled as
 * stale, and no product contract in this repository defines that presentation for a card titled
 * "KPIs en Tiempo Real". Inventing one here would be inventing semantics, so the card fails
 * closed instead — it keeps the honest statement that the feed was lost and publishes nothing.
 * A refusal already recorded is not downgraded to a mere disconnection.
 */
/**
 * ── P13-V5A-R5 · DIRECTOR FINAL REPAIR · FAILING CLOSED IS ONLY HALF OF THE TRUTH ──
 *
 * R5 stopped this card publishing when its producer disappears. It did not make it come back when
 * the producer returns, and the analytics channel is REQUEST-DRIVEN: the backend answers
 * `analytics:get_metrics` / `analytics:get_timeseries` (realtimeSocketServer.js:322,333) and
 * pushes nothing unsolicited. The only emitter of those requests is `manualRefresh()`, and its
 * control is hidden while the card is not READY — so
 *
 *     READY → disconnect → STALE → physical reconnect → STALE, indefinitely
 *
 * until a page reload. A legitimate canonical capability had been retired by the truth repair
 * itself, which is the over-retirement failure mode this mission tests for in both directions.
 *
 * THE SIGNAL. Not `isConnected` turning true — that flips on the FIRST connection too, and would
 * duplicate the mount-time acquisition. `RT_LOCAL.SOCKET_RECONNECT` is the canonical recovery
 * event established by P13-V1D R1/R2: the transport emits it exactly once per physical recovery,
 * never for the initial connection, and it also covers `connect_error` → later `connect`
 * (websocket.service.ts:76-89). This view adopts that conclusion rather than re-deriving one.
 *
 * WHAT A RECOVERY MAY AND MAY NOT DO. It starts a NEW acquisition — it never revives the old one.
 * A refusal is terminal and is not re-promoted; only an admissible answer to the new request can
 * reach `ready`. Reconnecting proves the transport is back, and nothing about authority or data.
 */
function reacquireAfterRecovery() {
  // A denial is terminal: transport recovery is not new authority.
  if (realtimeState.value === 'unauthorized') return
  // Already publishing: the live channel will keep answering; a second request would duplicate.
  if (realtimeState.value === 'ready') return

  // A genuinely NEW acquisition, with its own bounded wait. `stale` and `error` both qualify:
  // neither is a claim about authority, and both mean "we currently have nothing to publish".
  realtimeState.value = 'resolving'
  dropFigures()
  clearResolutionTimeout()
  manualRefresh()
  resolutionTimeout = setTimeout(() => failRealtimeAnalytics(), RESOLUTION_TIMEOUT_MS)
}

function markTelemetryLost() {
  if (realtimeState.value !== 'ready') return
  realtimeState.value = 'stale'
  dropFigures()
}

function exportKPIs() {
  // Defence in depth: the control is hidden, and a programmatic call still refuses. Gated on READY
  // rather than on `!denied`, because RESOLVING and ERROR are not authorized truth either.
  if (!canPublishRealtime.value) return
  const rows = ['tick,value']
  kpiTrend.value.forEach((v, i) => rows.push(`${i},${v}`))
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'kpis-realtime.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// Realtime wiring (WS + store)
const realtime = useRealtimeStore()
const isConnected = computed(() => realtime.isConnected)

// Thresholds / alerts
const alertThreshold = ref<number>(50)
const slaMin = ref<number>(90)
const autoUpdate = ref<boolean>(true)

const notify = ref(false)
const notifyText = ref('')
const notifyColor = ref<'warning' | 'error' | 'success'>('warning')

// A threshold cannot be breached by a figure nobody reported.
const alertsClass = computed(() => (alerts.value !== null && alerts.value >= alertThreshold.value ? 'text-error' : ''))
const slaClass = computed(() => (sla.value !== null && sla.value < slaMin.value ? 'text-error' : ''))

function savePrefs() {
  try {
    localStorage.setItem('rt_alertThreshold', String(alertThreshold.value))
    localStorage.setItem('rt_slaMin', String(slaMin.value))
  } catch {}
}

function loadPrefs() {
  try {
    const at = localStorage.getItem('rt_alertThreshold')
    const sm = localStorage.getItem('rt_slaMin')
    if (at) alertThreshold.value = Number(at)
    if (sm) slaMin.value = Number(sm)
  } catch {}
}

/**
 * P13-V5A-R3 · DIRECTOR_REPAIR_R1 — this view subscribes to the canonical analytics socket DIRECTLY
 * (bypassing the store), and it is routed for `group_manager` as well as `super_admin`. K4A admits
 * only a current persisted `super_admin` and answers everyone else with `{ denied: true }`.
 *
 * Before this repair `handleMetrics` read `m.eventsByType || {}` from that refusal, derived
 * `possibleAlert = 0` and wrote `alerts.value = 0` — turning an authorization DENIAL into a
 * business figure of zero. A denial is not a measurement of zero alerts.
 */
function isDeniedPayload(payload: any): boolean {
  return !!payload && typeof payload === 'object' && payload.denied === true
}

/**
 * P13-V5A-R6 — the window THIS view asked for.
 *
 * `/reports/analytics-suite` mounts `RealtimeAnalyticsWidget` (whose store asks the same
 * untargeted `analytics:timeseries` channel for `events`/`1h`) directly above this view, which
 * asks for `events`/`10m`. The channel has no correlation id, so both replies reach both
 * listeners; without this check the sparkline below concatenated two different measurement windows
 * into a single line. Every number in it was real — and the line still said something untrue,
 * because a series attributed to a period it was not measured over is a fabricated fact.
 *
 * The server echoes `{ metric, duration, data }` (`realtimeSocketServer.js:339`), so the answer
 * already carries the question it belongs to. No protocol change is required and none is made.
 */
const REQUESTED_SERIES = { metric: 'events', duration: '10m' } as const

/**
 * P13-V5A-R6 FINAL — attribution is REQUIRED, not merely checked when offered.
 *
 * The canonical producer for this channel always emits `{ metric, duration, data }` on success
 * (`realtimeSocketServer.js:339`) and `{ denied: true }` on refusal; nothing else emits it, and the
 * REST timeseries surface has no consumer here. An unattributable success therefore has no
 * legitimate origin — and IS exactly the shape that could belong to the widget mounted above this
 * view on `/reports/analytics-suite`. Both fields must match; partial attribution is not
 * attribution.
 */
function isOurSeries(payload: any): boolean {
  return payload?.metric === REQUESTED_SERIES.metric && payload?.duration === REQUESTED_SERIES.duration
}

function handleTimeseries(payload: any) {
  // A refusal is honoured even with auto-update off: authority is not a display preference. It is
  // checked BEFORE the series filter deliberately — a denial carries no metric/duration, and an
  // authorisation refusal must never be discarded as "somebody else's answer".
  if (isDeniedPayload(payload)) { refuseRealtimeAnalytics(); return }
  // A timeseries proves a SERIES, not authority over the alerts/SLA figures, so it never promotes
  // the card out of `resolving`; and while unresolved its points are discarded rather than banked.
  if (!canPublishRealtime.value) return
  if (!isOurSeries(payload)) return
  if (!autoUpdate.value) return
  const data = Array.isArray(payload?.data) ? payload.data : []
  // Map a valores numéricos
  const nums = data.map((p: any) => Number(p.v ?? p.value ?? 0)).filter((n: any) => !isNaN(n))
  if (nums.length) {
    kpiTrend.value = [...kpiTrend.value, ...nums].slice(-40)
  }
}

function handleMetrics(data: any) {
  if (isDeniedPayload(data) || isDeniedPayload(data?.metrics)) {
    refuseRealtimeAnalytics()
    return
  }
  // ADMISSIBILITY, delegated to the shared validator. An arbitrary object, an empty object or a
  // transport acknowledgement is not telemetry and must not promote the card to `ready`.
  const m = normalizeTelemetry(data?.metrics ?? data)
  if (!m) return

  // The acquisition is positively resolved: from here the card may publish. A card demoted to
  // `stale` may return to `ready` on fresh admissible telemetry — but a refusal or a failed
  // acquisition is terminal and is never re-promoted here.
  if (realtimeState.value === 'resolving' || realtimeState.value === 'stale') {
    realtimeState.value = 'ready'
    clearResolutionTimeout()
  }
  if (realtimeState.value !== 'ready') return
  if (!autoUpdate.value) return

  // Derivar "alertas" SÓLO si la telemetría realmente trae la serie; su ausencia no es un cero.
  const byType = m.eventsByType
  if (byType && typeof byType === 'object') {
    const possibleAlert = byType.alert ?? byType.critical
    if (typeof possibleAlert === 'number') alerts.value = possibleAlert
  }
  // SLA derivado simple si viene un campo, si no, mantener
  if (typeof m.sla === 'number') sla.value = Math.round(m.sla)
}

function manualRefresh() {
  websocketService.requestAnalyticsMetrics()
  websocketService.requestAnalyticsTimeSeries('events', '10m')
}

// Inventario · Reporte ejecutivo
const inv = ref<any | null>(null)
const invLoading = ref(false)
const invError = ref<string | null>(null)
const invDebugUrl = ref<string | null>(null)
const invDebugStatus = ref<number | null>(null)
const invDebugMethod = ref<string | null>(null)

/**
 * P13-V7B (refutation) — THE EXECUTIVE REPORT, READ WHERE ITS PRODUCER WRITES IT.
 *
 * `GET /inventory/reports/executive` (reportsController.getExecutiveReport) answers
 * `{ summary: { totalItems, totalStock, lowStockItems, … }, categories: [{ category, totalStock, … }],
 *    alerts: { active: [<sampled alert>], summary: { total, critical, warning, info, … } } }`.
 * Each card takes its figure from that shape; a figure that is not a finite number is `null` and
 * renders "Sin datos" — a real 0 from the server still renders 0.
 */
const statedNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}
const statText = (value: number | null): string => (value === null ? 'Sin datos' : String(value))

const invFigures = computed(() => {
  const report: any = inv.value
  const summary: any = report?.summary && typeof report.summary === 'object' ? report.summary : null
  const categories = Array.isArray(report?.categories)
    ? report.categories.map((c: any) => ({ category: String(c?.category ?? '—'), totalStock: statedNumber(c?.totalStock) }))
    : null
  return {
    totalItems: statedNumber(summary?.totalItems),
    totalStock: statedNumber(summary?.totalStock),
    lowStockItems: statedNumber(summary?.lowStockItems),
    activeAlerts: statedNumber(report?.alerts?.summary?.total),
    categories
  }
})

async function loadExecutiveInventory() {
  try {
    invError.value = null
    invDebugUrl.value = null
    invDebugStatus.value = null
    invDebugMethod.value = null
    invLoading.value = true
    const { data } = await InventoryService.getExecutiveReport({ period: '30d', includeInactive: false })
    inv.value = data
  } catch (e: any) {
    // P13-V7B (refutation): a failed refresh withdraws the previous report instead of leaving its
    // figures on screen under the error banner.
    inv.value = null
    invError.value = e?.message ?? 'No se pudo cargar el reporte ejecutivo de inventario'
    try {
      const cfg = e?.config || e?.response?.config
      const base = cfg?.baseURL || ''
      const path = cfg?.url || ''
      const method = (cfg?.method || 'get').toUpperCase()
      const url = (base?.endsWith('/') ? base.slice(0, -1) : base) + (path?.startsWith('/') ? path : ('/' + path))
      invDebugUrl.value = url
      invDebugStatus.value = e?.response?.status ?? null
      invDebugMethod.value = method
    } catch {}
  } finally {
    invLoading.value = false
  }
}

// -----------------------------
// Reportes de Inventario (7.2)
// -----------------------------
const invTabs = ref<'alerts'>('alerts')

// Alertas
const alLoading = ref(false)
const alError = ref<string | null>(null)
const alertsReport = ref<any | null>(null)
const alSeverity = ref<string>('')
const alResolved = ref<boolean>(false)
async function loadAlertsReport() {
  try {
    alError.value = null
    alLoading.value = true
    const { data } = await InventoryService.getAlertsReport({ severity: alSeverity.value || undefined, resolved: alResolved.value })
    alertsReport.value = data
  } catch (e: any) {
    // P13-V7B (refutation): the report of the PREVIOUS filter must not stay on screen as the answer
    // to the one that just failed.
    alertsReport.value = null
    alError.value = e?.message ?? 'No se pudo cargar el reporte de alertas'
  } finally {
    alLoading.value = false
  }
}

/** P13-V7B (refutation): `GET /inventory/reports/alerts` → `summary.{total,critical,warning,info}`. */
const alertsSummary = computed(() => {
  const s: any = alertsReport.value?.summary
  if (!s || typeof s !== 'object') return null
  return {
    total: statedNumber(s.total),
    critical: statedNumber(s.critical),
    warning: statedNumber(s.warning),
    info: statedNumber(s.info)
  }
})

function exportCsv(filename: string, rows: string[]) {
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function exportAlertsCsv() {
  const rows: string[] = ['id,type,severity,isResolved,product,createdAt']
  const list = alertsReport.value?.alerts || []
  list.forEach((a: any) => rows.push(`${a.id},${a.type},${a.severity},${a.isResolved},"${a.product?.name || ''}",${a.createdAt}`))
  exportCsv('inventario-alertas.csv', rows)
}

onMounted(async () => {
  loadPrefs()
  // NO synthetic seed: the card starts with nothing to publish and stays that way until an
  // admissible telemetry answer arrives. (This used to plant 20 Math.random points on mount.)

  // Listeners WS — attached BEFORE the request so no answer can be missed.
  websocketService.on('analytics:timeseries', handleTimeseries)
  websocketService.on('analytics:metrics', handleMetrics)
  // The analytics channel is REQUEST-DRIVEN, so a recovery has to ASK. Without this the card
  // stays stale after the transport returns, until a page reload.
  websocketService.on(RT_LOCAL.SOCKET_RECONNECT, reacquireAfterRecovery)

  // Conexión realtime si no está
  let connectFailed = false
  try {
    if (!realtime.isConnected) {
      await realtime.connect()
    }
  } catch (e) {
    // A transport failure is NOT permission to fabricate live KPIs.
    connectFailed = true
  }

  if (connectFailed) {
    failRealtimeAnalytics()
  } else {
    // Primer fetch por socket
    manualRefresh()
    // Bounded wait: if nothing admissible answers, the acquisition failed — it did not succeed
    // quietly with defaults, and it is not left spinning forever either.
    //
    // P13-V5A-R5 · DIRECTOR FINAL REPAIR: clear before arming. A reconnect can fire DURING the
    // awaited `realtime.connect()` above (the transport recovers an earlier socket while this
    // mount is still in flight), and `reacquireAfterRecovery()` arms its own timeout. Without
    // this clear the earlier one is orphaned and can later fire against a NEWER acquisition,
    // marking a live one failed and stranding the card on an error it did not earn.
    clearResolutionTimeout()
    resolutionTimeout = setTimeout(() => failRealtimeAnalytics(), RESOLUTION_TIMEOUT_MS)
  }
  // Primer fetch inventario
  loadExecutiveInventory()
  // Cargar datos iniciales de la pestaña activa (auto-load 7.2 — solo superficies reales)
  if (invTabs.value === 'alerts') {
    await loadAlertsReport()
  }

  // P13-V5A-R5: ya no existe simulador de respaldo. La única fuente de estas cifras es la
  // telemetría del socket canónico; si se pierde, la tarjeta pasa a `stale` y deja de publicar.
})

onUnmounted(() => {
  clearResolutionTimeout()
  websocketService.off('analytics:timeseries', handleTimeseries as any)
  websocketService.off('analytics:metrics', handleMetrics as any)
  websocketService.off(RT_LOCAL.SOCKET_RECONNECT, reacquireAfterRecovery as any)
})

// Alertas por umbral — P13-V5A-R5: una alerta operativa sólo puede dispararse desde una cifra
// que el servidor reportó. Antes el simulador la disparaba con valores inventados.
watch([alerts, alertThreshold], ([val, thr]) => {
  savePrefs()
  if (val !== null && (val as number) >= (thr as number)) {
    notifyText.value = `⚠️ Alertas ≥ ${thr} (actual: ${val})`
    notifyColor.value = 'error'
    notify.value = true
  }
})

watch([sla, slaMin], ([val, min]) => {
  savePrefs()
  if (val !== null && (val as number) < (min as number)) {
    notifyText.value = `⚠️ SLA por debajo del mínimo (${val}% < ${min}%)`
    notifyColor.value = 'warning'
    notify.value = true
  }
})

/**
 * P13-V5A-R5 — the feed is the producer, so losing it must change what the card says.
 * `isConnected` is the realtime store's own connection state; when it drops after the card has
 * been publishing, the figures on screen are no longer current and are withdrawn.
 */
watch(isConnected, (connected) => {
  if (!connected) markTelemetryLost()
})

// Auto-carga por pestaña (7.2): cargar al cambiar de tab si no hay datos
watch(invTabs, async (tab) => {
  if (tab === 'alerts' && !alertsReport.value && !alLoading.value) {
    await loadAlertsReport()
  }
})

// Auto-reload al cambiar filtros cuando la pestaña correspondiente está activa
watch([alSeverity, alResolved], async () => {
  if (invTabs.value === 'alerts' && !alLoading.value) {
    await loadAlertsReport()
  }
})
</script>
